import React, { useEffect, useState } from "react";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "@/components/ui/button";
import MentionTextarea from "@/estimating/components/MentionTextarea";
import { clearMentions } from "@/estimating/lib/mentions";

// RA note (2026-10) — the job's Notes tab: the same stamped, never-edited thread the RA grid's Notes
// column shows (shared with Operations › Jobs). Newest first; @name mentions ring the header bell.
const stampOf = (n) => {
  const d = n.at ? new Date(n.at).toLocaleDateString("en-US", { month: "numeric", day: "numeric" }) : "";
  return [n.initials, d].filter(Boolean).join(" ") || "Earlier";
};

export default function ProjectNotesTab({ projectId }) {
  const [notes, setNotes] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/projects/${projectId}/notes`).then((r) => setNotes(r.data || [])).catch(() => setNotes([]));
  useEffect(() => { load(); clearMentions({ field_project_id: projectId }); /* eslint-disable-next-line */ }, [projectId]);
  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/notes`, { text: text.trim() });
      setText("");
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not post the note"); }
    finally { setBusy(false); }
  };
  return (
    <div className="max-w-3xl rounded-lg border border-gray-200 bg-white p-4" data-testid="ra-notes-tab">
      <MentionTextarea autoFocus rows={3} value={text} onChange={setText} onSubmit={submit}
                       placeholder="Add a note — @name to mention someone (stamped, never edited)"
                       className="text-sm" testid="ra-notes-input" />
      <div className="mt-2 mb-4 flex items-center justify-between">
        <span className="text-[11px] text-gray-500">Enter posts · Shift+Enter for a new line · shared with Operations › Jobs</span>
        <Button size="sm" className="h-8 text-xs" onClick={submit} disabled={busy || !text.trim()} data-testid="ra-notes-submit">
          <Send className="mr-1 h-3 w-3" /> Post
        </Button>
      </div>
      {notes === null ? <div className="text-sm text-gray-500">Loading…</div> : (
        <ul className="space-y-2" data-testid="ra-notes-thread">
          {notes.map((n) => (
            <li key={n.id} className="rounded border border-gray-200 p-2.5" data-testid={`ra-note-${n.id}`}>
              <div className="text-[11px] text-gray-500"><span className="font-semibold text-gray-900">{stampOf(n)}</span> {n.user}</div>
              <div className="mt-0.5 whitespace-pre-wrap text-sm text-gray-900">{n.text}</div>
            </li>
          ))}
          {!notes.length && <li className="text-sm text-gray-500" data-testid="ra-notes-empty">No notes yet.</li>}
        </ul>
      )}
    </div>
  );
}
